import React, { useEffect, useState, useRef, useCallback } from "react";
import { useWebSocket } from "@/context/WebSocketContext";
import { getWebSocketChannelUrl } from "@/utils/websocket";
import { ConnectionState } from "@/services/WebSocketManager";
import { motion, AnimatePresence } from "framer-motion";
import { LoadingIndicator } from "./indicators/LoadingIndicator";

interface TaskStatus {
  state: string;
  timestamp: string;
  message: string | null;
}

interface TaskData {
  uuid: string;
  name: string;
  status: {
    name: string;
    colour: string;
  };
  taskable: {
    name: string;
    type: string;
  };
  completed_by?: {
    name: string;
  };
  completed_at?: string;
}

interface PlannrActivity {
  operation: string;
  timestamp: string;
  document_id: string;
  database: string;
  full_document: {
    task_uuid: string;
    activity_type: string;
    data: TaskData;
    status: TaskStatus;
    updated_at: string;
  };
}

const PLANNR_WS_CHANNEL = getWebSocketChannelUrl("plannr_activities");
const RECONNECT_DELAY = 5000; // 5 seconds

const ActivityCard: React.FC<{ activity: PlannrActivity; index: number }> = ({
  activity,
  index,
}) => {
  const taskData = activity?.full_document?.data;
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500 + index * 300);

    return () => clearTimeout(timer);
  }, [index]);

  if (!taskData) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 20, scale: 0.95 }}
      transition={{ duration: 0.5, delay: index * 0.2 }}
      className=" pl-3 py-4   relative min-h-[120px]"
      style={{ borderColor: taskData.status?.colour || "#3b82f6" }}
    >
      {isLoading ? (
        <div className="absolute inset-0 bg-gray-50/95 flex flex-col items-center justify-center gap-4 z-10 backdrop-blur-sm rounded-r-lg">
          <LoadingIndicator />
          <div className="flex flex-col items-center gap-1">
            <span className="text-sm font-medium text-gray-700">
              Loading activity data
            </span>
            <span className="text-xs text-gray-500 animate-pulse">
              Please wait...
            </span>
          </div>
        </div>
      ) : (
        <>
          <div className="flex justify-between items-start">
            <motion.div
              className="text-sm font-medium text-gray-800"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              {taskData.name || "Unnamed Task"}
            </motion.div>
            <motion.div
              className="text-xs text-gray-500"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {new Date(activity.timestamp).toLocaleTimeString()}
            </motion.div>
          </div>

          {taskData.taskable && (
            <motion.div
              className="text-sm text-gray-600 mt-1"
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              {taskData.taskable.name} ({taskData.taskable.type})
            </motion.div>
          )}

          <div className="flex justify-between items-center mt-1">
            {taskData.status && (
              <motion.div
                className="text-xs px-2 py-1 rounded-full"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 }}
                style={{
                  backgroundColor: `${taskData.status.colour}15`,
                  color: taskData.status.colour || "#666",
                }}
              >
                {taskData.status.name}
              </motion.div>
            )}
            {taskData.completed_by && (
              <motion.div
                className="text-xs text-gray-500 flex items-center gap-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <svg
                  className="w-3 h-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                </svg>
                {taskData.completed_by.name}
              </motion.div>
            )}
          </div>
        </>
      )}
    </motion.div>
  );
};

// Loading indicator component
// const LoadingIndicator: React.FC<{
//   color?: string;
//   size?: "sm" | "md" | "lg";
// }> = ({ color = "#3b82f6", size = "md" }) => {
//   const sizeClasses = {
//     sm: "w-16",
//     md: "w-24",
//     lg: "w-32",
//   };

//   const heightClasses = {
//     sm: "h-1.5",
//     md: "h-2",
//     lg: "h-2.5",
//   };

//   // Convert hex to RGB for gradient
//   const hexToRgb = (hex: string) => {
//     const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
//     return result
//       ? {
//           r: parseInt(result[1], 16),
//           g: parseInt(result[2], 16),
//           b: parseInt(result[3], 16),
//         }
//       : null;
//   };

//   const rgb = hexToRgb(color);
//   const gradientColor = rgb
//     ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.2)`
//     : "rgba(99, 102, 241, 0.2)";

//   return (
//     <motion.div
//       className="flex justify-center items-center overflow-hidden"
//       initial={{ opacity: 0 }}
//       animate={{ opacity: 1 }}
//       exit={{ opacity: 0 }}
//     >
//       <div
//         className={`relative ${sizeClasses[size]} ${heightClasses[size]} bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden shadow-inner`}
//       >
//         {/* Background pulse effect */}
//         <motion.div
//           className="absolute inset-0"
//           style={{
//             background: `linear-gradient(90deg, transparent 0%, ${gradientColor} 50%, transparent 100%)`,
//           }}
//           animate={{
//             x: ["-100%", "100%"],
//           }}
//           transition={{
//             duration: 1.5,
//             repeat: Infinity,
//             ease: "linear",
//           }}
//         />

//         {/* Main progress bar */}
//         <motion.div
//           className="absolute top-0 left-0 bottom-0 rounded-full"
//           style={{
//             background: `linear-gradient(90deg,
//               transparent 0%,
//               ${color} 20%,
//               ${color} 80%,
//               transparent 100%
//             )`,
//             width: "50%",
//           }}
//           animate={{
//             x: ["-100%", "200%"],
//             scale: [0.8, 1, 0.8],
//           }}
//           transition={{
//             duration: 2,
//             repeat: Infinity,
//             ease: "easeInOut",
//           }}
//         />

//         {/* Highlight effect */}
//         <motion.div
//           className="absolute top-0 left-0 bottom-0 rounded-full"
//           style={{
//             background: `linear-gradient(90deg,
//               transparent 0%,
//               rgba(255, 255, 255, 0.8) 50%,
//               transparent 100%
//             )`,
//             width: "30%",
//           }}
//           animate={{
//             x: ["-100%", "400%"],
//             scale: [1, 1.2, 1],
//           }}
//           transition={{
//             duration: 2,
//             repeat: Infinity,
//             ease: "easeInOut",
//             delay: 0.4,
//           }}
//         />

//         {/* Shimmer effect */}
//         <motion.div
//           className="absolute inset-0"
//           style={{
//             background: `linear-gradient(90deg,
//               transparent 0%,
//               rgba(255, 255, 255, 0.1) 50%,
//               transparent 100%
//             )`,
//           }}
//           animate={{
//             x: ["-100%", "100%"],
//           }}
//           transition={{
//             duration: 1,
//             repeat: Infinity,
//             ease: "linear",
//             delay: 0.2,
//           }}
//         />
//       </div>
//     </motion.div>
//   );
// };

const SyncStatus: React.FC<{
  lastSyncTime: Date | null;
  isConnecting: boolean;
  getTimeSinceLastSync: () => string;
  connectionStatus: ConnectionState;
}> = ({
  lastSyncTime,
  isConnecting,
  getTimeSinceLastSync,
  connectionStatus,
}) => {
  const [dots, setDots] = useState("");
  const [showLoading, setShowLoading] = useState(true);

  useEffect(() => {
    if (isConnecting) {
      const interval = setInterval(() => {
        setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
      }, 400);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [isConnecting]);

  useEffect(() => {
    if (!isConnecting) {
      const timer = setTimeout(() => {
        setShowLoading(false);
      }, 2000);
      return () => clearTimeout(timer);
    } else {
      setShowLoading(true);
    }
  }, [isConnecting]);

  return (
    <motion.div
      className="flex items-center gap-3"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      key={getTimeSinceLastSync()}
    >
      <div className="flex flex items-center">
        <motion.div
          className={` py-2 rounded-full flex items-center gap-3 `}
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{
            type: "spring",
            stiffness: 200,
            damping: 10,
          }}
        >
          {connectionStatus === ConnectionState.CONNECTING ? (
            <LoadingIndicator />
          ) : (
            <></>
          )}
        </motion.div>
        <AnimatePresence mode="wait">
          <motion.span
            className=" text-gray-900"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            key={isConnecting || showLoading ? "Syncing" : "Synced"}
          >
            {isConnecting || showLoading ? (
              <span className="inline-flex items-center">
                Syncing...
                <span className="text-indigo-500">{dots}</span>
              </span>
            ) : (
              "Synced"
            )}
          </motion.span>
        </AnimatePresence>
        {/* <span className="text-sm text-gray-500">
          {lastSyncTime ? getTimeSinceLastSync() : "..."}
        </span> */}
      </div>
    </motion.div>
  );
};

export const PlannrTracker: React.FC = () => {
  const { subscribe, unsubscribe, status, lastMessage, connect } =
    useWebSocket();
  const [activities, setActivities] = useState<PlannrActivity[]>([]);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [updateCount, setUpdateCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const isComponentMounted = useRef(true);
  const reconnectTimeout = useRef<number | null>(null);

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeout.current) {
      window.clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }
  }, []);

  const handleConnectionError = useCallback(() => {
    if (!isComponentMounted.current) return;

    setConnectionError("Connection lost. Attempting to reconnect...");
    clearReconnectTimeout();

    // Attempt to reconnect after delay
    reconnectTimeout.current = window.setTimeout(() => {
      if (isComponentMounted.current) {
        setConnectionError("Reconnecting...");
        connect(PLANNR_WS_CHANNEL);
      }
    }, RECONNECT_DELAY);
  }, [connect, clearReconnectTimeout]);

  useEffect(() => {
    isComponentMounted.current = true;

    // Initial connection
    if (isComponentMounted.current) {
      subscribe(PLANNR_WS_CHANNEL);
    }

    return () => {
      isComponentMounted.current = false;
      clearReconnectTimeout();
      unsubscribe(PLANNR_WS_CHANNEL);
    };
  }, [subscribe, unsubscribe, clearReconnectTimeout]);

  // Monitor connection status changes
  useEffect(() => {
    const currentStatus = status?.get(PLANNR_WS_CHANNEL)?.state;
    console.log("WebSocket status changed:", currentStatus);

    if (currentStatus === ConnectionState.CONNECTED) {
      console.log("WebSocket connected successfully");
      setConnectionError(null);
      clearReconnectTimeout();
    } else if (currentStatus === ConnectionState.DISCONNECTED) {
      console.log("WebSocket disconnected, initiating reconnection");
      handleConnectionError();
    }
  }, [status, handleConnectionError, clearReconnectTimeout]);

  const getTimeSinceLastSync = () => {
    if (!lastSyncTime) return "";
    const seconds = Math.floor(
      (new Date().getTime() - lastSyncTime.getTime()) / 1000
    );
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ago`;
  };

  useEffect(() => {
    if (!isComponentMounted.current) return;

    const message = lastMessage?.get(PLANNR_WS_CHANNEL);
    console.log("Received WS message:", message);

    if (message) {
      try {
        const newActivity =
          typeof message === "string" ? JSON.parse(message) : message;
        console.log("Parsed activity:", newActivity);

        if (!newActivity?.full_document) {
          console.warn(
            "Invalid activity structure - missing full_document:",
            newActivity
          );
          return;
        }

        setUpdateCount((prev) => prev + 1);
        setLastSyncTime(new Date());

        setActivities((prev) => {
          const isDuplicate = prev.some(
            (a) => a.document_id === newActivity.document_id
          );
          if (isDuplicate) {
            return prev.map((a) =>
              a.document_id === newActivity.document_id ? newActivity : a
            );
          }
          return [newActivity, ...prev].slice(0, 10);
        });
      } catch (error) {
        console.error("Error handling Plannr activity:", error);
      }
    }
  }, [lastMessage]);

  // Update the "time ago" text every second
  useEffect(() => {
    const interval = setInterval(() => {
      if (lastSyncTime) {
        // Force re-render to update the time ago text
        setLastSyncTime((prev) => (prev ? new Date(prev.getTime()) : null));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const connectionStatus =
    status?.get(PLANNR_WS_CHANNEL)?.state || ConnectionState.DISCONNECTED;

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "text-green-600";
      case "in_progress":
        return "text-blue-600";
      case "error":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="relative "
    >
      <div className="absolute right-6 top-6">
        <img
          src="/images/plannr_icon.jpg"
          alt="Plannr Icon"
          className="w-8 h-8 "
        />
      </div>
      <div className="flex flex-col gap-4">
        {/* Header Section */}
        <div className="flex items-center justify-between">
          <div className="p-6 pb-0">
            <div className="text-3xl font-bold">
              <SyncStatus
                lastSyncTime={lastSyncTime}
                isConnecting={connectionStatus === ConnectionState.CONNECTING}
                getTimeSinceLastSync={getTimeSinceLastSync}
                connectionStatus={connectionStatus}
              />
            </div>
            <h3 className=" font-mono mb-4 flex items-center gap-3 text-gray-500">
              <span
                className={`w-2.5 h-2.5 rounded-full ${
                  connectionStatus === ConnectionState.CONNECTED
                    ? "bg-green-500"
                    : connectionStatus === ConnectionState.RECONNECTING
                    ? "bg-yellow-500"
                    : "bg-red-500"
                }`}
              />
              Plannr{" "}
              {connectionStatus.charAt(0).toUpperCase() +
                connectionStatus.slice(1).toLowerCase()}
            </h3>
          </div>
        </div>

        {/* Stats Section */}
        <div className="flex items-center gap-6 px-1">
          {/* <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex items-center gap-3"
          >
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <svg
                className="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-2xl font-medium text-gray-900">
                {updateCount}
              </span>
              <span className="text-sm text-gray-500">updates</span>
            </div>
          </motion.div> */}
          {/* 
          <SyncStatus
            lastSyncTime={lastSyncTime}
            isConnecting={connectionStatus === ConnectionState.CONNECTING}
            getTimeSinceLastSync={getTimeSinceLastSync}
            connectionStatus={connectionStatus}
          /> */}

          {connectionError && (
            <motion.div
              className="text-sm text-orange-600 flex items-center gap-3 ml-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {connectionError}
              {connectionStatus === ConnectionState.CONNECTING && (
                <LoadingIndicator />
              )}
            </motion.div>
          )}
        </div>
      </div>

      {/* <div className="space-y-4 mt-8">
        <AnimatePresence mode="popLayout">
          {activities.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-gray-600 font-mono text-center py-12 flex flex-col items-center gap-6 bg-gray-50 rounded-lg"
            >
              {connectionStatus === ConnectionState.CONNECTED ? (
                "No recent activity"
              ) : (
                <>
                  <LoadingIndicator />
                  <span className="text-sm">Waiting for connection...</span>
                </>
              )}
            </motion.div>
          ) : (
            activities.map((activity, index) => (
              <ActivityCard
                key={activity.document_id}
                activity={activity}
                index={index}
              />
            ))
          )}
        </AnimatePresence>
      </div> */}
    </motion.div>
  );
};
